import {
  PDF_FONT_SIZE,
  PDF_HEADER_BACKGROUND_COLOR,
  type ReportTableCell,
  type ReportTableProps,
} from '@/components';
import {
  type ReportOrganizingTableFormat1Model,
  type ReportOrganizingTableFormat1PolicesModel,
  format1ColumnType,
  type projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { t } from '@/i18n';
import {
  BigCalculator,
  formatAmountForPdf,
  formatProjectEndYearForPdf,
  formatProjectStartYearForPdf,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../../pdf/pdf-utils';
import {
  type SummaryTotalType,
  format1HeaderModifier,
  format1ValueConverter,
} from './get-csv';

type TableBodyElement = ReportTableProps['data'][number];

const headerStyle: CSSProperties = {
  fontSize: PDF_FONT_SIZE.MD,
  textAlign: 'center',
};

const MONEY_RATIO = 0.9;
const MONEY_STYLE: CSSProperties = { textAlign: 'right' };

const getFormat1PdfHeader = ({
  year,
}: {
  year: number;
}): ReportTableProps['header'] => {
  const header = format1HeaderModifier({ currentYear: year });
  return [
    { value: header.policyLevel1, ratio: 0.3, textStyle: headerStyle },
    { value: header.policyLevel2, ratio: 0.3, textStyle: headerStyle },
    { value: header.projectNumber, ratio: 0.45, textStyle: headerStyle },
    { value: header.name, ratio: 1.2, textStyle: headerStyle },
    { value: header.parentOrganization, ratio: 1, textStyle: headerStyle },
    { value: header.startYear, ratio: 0.4, textStyle: headerStyle },
    { value: header.endYear, ratio: 0.4, textStyle: headerStyle },
    { value: header.accountTitle, ratio: 1, textStyle: headerStyle },
    {
      type: 'multiple',
      ratio: MONEY_RATIO * 2,
      cell: {
        vertical: true,
        cellColRatios: [MONEY_RATIO, MONEY_RATIO],
        flexCells: [
          {
            value: header.priorUsableFinalizedAmountTotal,
            textStyle: headerStyle,
          },
          {
            type: 'multiple',
            cell: {
              flexCells: [
                { value: header.executableAmount, textStyle: headerStyle },
                { value: header.executionAmount, textStyle: headerStyle },
              ],
            },
          },
        ],
      },
    },
    {
      value: header.initialBudgetTotal,
      ratio: MONEY_RATIO,
      textStyle: headerStyle,
    },
    {
      value: header.posteriorRequestedAmountTotal,
      ratio: MONEY_RATIO,
      textStyle: headerStyle,
    },
    {
      value: header.differencePosteriorRequestedSelfInitialBudget,
      ratio: MONEY_RATIO,
      textStyle: headerStyle,
    },
    { value: header.externalOpinion, ratio: 3, textStyle: headerStyle },
    { value: header.adminReformReview, ratio: 1, textStyle: headerStyle },
    {
      type: 'multiple',
      ratio: BigCalculator.add(MONEY_RATIO, 1),
      cell: {
        vertical: true,
        flexCells: [
          { value: header.reflectionStatus, textStyle: headerStyle },
          {
            type: 'multiple',
            cell: {
              cellColRatios: [MONEY_RATIO, 1],
              flexCells: [
                { value: header.reflectedAmount, textStyle: headerStyle },
                { value: header.reflectedContents, textStyle: headerStyle },
              ],
            },
          },
        ],
      },
    },
    {
      value: header.externalEvaluationScope,
      ratio: 1,
      textStyle: headerStyle,
    },
    {
      value: header.externalEvaluationReason,
      ratio: 0.9,
      textStyle: headerStyle,
    },
    {
      value: header.externalEvaluationLastExecutionYear,
      ratio: 0.6,
      textStyle: headerStyle,
    },
  ];
};

const getForma1PdfBody = ({
  policies,
  isDebugMode = false,
  projectDetailUrlType,
}: {
  policies: Array<z.infer<typeof ReportOrganizingTableFormat1PolicesModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): ReportTableProps['data'] => {
  return policies.flatMap<TableBodyElement>((policyLevel1) => {
    return [
      {
        rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
        row: [
          {
            value: policyLevel1.policyName ?? t('not-set'),
            colSpan: 18,
          },
        ],
      },
      ...policyLevel1.policyMeasuresData.flatMap<TableBodyElement>(
        (policyLevel2) => {
          return [
            {
              rowStyle: { backgroundColor: PDF_HEADER_BACKGROUND_COLOR },
              row: [
                { value: '' },
                {
                  value: policyLevel2.measureName ?? t('not-set'),
                  colSpan: 17,
                },
              ],
            },
            ...policyLevel2.projectsData.flatMap<TableBodyElement>(
              (content) => ({
                row: format1ColumnType.options.flatMap<ReportTableCell>(
                  (key) => {
                    switch (key) {
                      case 'policyLevel1':
                      case 'policyLevel2': {
                        return { value: '' };
                      }
                      case 'startYear': {
                        return {
                          value: formatProjectStartYearForPdf(content[key]),
                        };
                      }
                      case 'endYear': {
                        return {
                          value: formatProjectEndYearForPdf(content[key]),
                        };
                      }
                      case 'accountTitle': {
                        return {
                          value: format1ValueConverter.accountingType(
                            content.accountingType,
                            content.accountTitle,
                          ),
                        };
                      }
                      case 'parentOrganization': {
                        return {
                          value: content[key],
                          textStyle: {
                            display: '-webkit-box',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        };
                      }
                      case 'externalEvaluationScope': {
                        return {
                          value: format1ValueConverter.externalEvaluationScope(
                            content[key],
                          ),
                        };
                      }
                      case 'externalEvaluationReason': {
                        return {
                          value: format1ValueConverter.externalEvaluationReason(
                            content[key],
                          ),
                        };
                      }
                      case 'adminReformReview': {
                        return {
                          value: format1ValueConverter.adminReformReviewOption(
                            content.adminReformReview.opinion,
                          ),
                        };
                      }
                      case 'externalOpinion': {
                        return {
                          value: format1ValueConverter.externalOpinion(
                            content.externalOpinion,
                            content.externalEvaluationScope,
                          ),
                        };
                      }
                      case 'name': {
                        return {
                          type: 'link',
                          href: getProjectDetailPageUrl({
                            isPS: projectDetailUrlType === 'public',
                            projectId: content.id,
                          }),
                          value: content.name,
                        };
                      }
                      case 'executionAmount':
                      case 'reflectedContents':
                      case 'projectDetailPageUrl': {
                        return [];
                      }
                      case 'executableAmount': {
                        return {
                          type: 'multiple',
                          cell: {
                            flexCells: [
                              {
                                ...centerTextStyle,
                                value:
                                  format1ValueConverter.previousFinalizedAmountTotal(
                                    {
                                      value:
                                        content.previousFinalizedAmountTotal,
                                      type: 'pdf',
                                      negativeCount:
                                        content.negativePreviousFinalizedAmountCount,
                                      isDebugMode,
                                      rawValue:
                                        content.rawPreviousFinalizedAmountTotal,
                                    },
                                  ),
                                textStyle: MONEY_STYLE,
                              },
                              {
                                ...centerTextStyle,
                                value: format1ValueConverter.executableAmount({
                                  value: content.previousExecutionAmountTotal,
                                  negativeCount:
                                    content.negativePreviousYearExecutionAmountCount,
                                  type: 'pdf',
                                  isDebugMode,
                                  rawValue:
                                    content.rawPreviousExecutionAmountTotal,
                                }),
                                textStyle: MONEY_STYLE,
                              },
                            ],
                          },
                        };
                      }
                      case 'reflectedAmount': {
                        return {
                          type: 'multiple',
                          cell: {
                            flexCells: [
                              {
                                wrapperStyle: { display: 'flex' },
                                textWrapperStyle: { width: '100%' },
                                value: format1ValueConverter.reflectedAmount({
                                  value:
                                    content.reflectionStatus.reflectedAmount,
                                  type: 'pdf',
                                }),
                                textStyle: MONEY_STYLE,
                              },
                              {
                                wrapperStyle: { display: 'flex' },
                                textWrapperStyle: { width: '100%' },
                                value:
                                  format1ValueConverter.reflectionContentAction(
                                    content.reflectionStatus.reflectionContent
                                      .action,
                                  ),
                              },
                            ],
                          },
                        };
                      }
                      case 'differencePosteriorRequestedSelfInitialBudget': {
                        return {
                          value:
                            format1ValueConverter.differencePosteriorRequestedSelfInitialBudget(
                              {
                                value:
                                  content.differencePosteriorRequestedSelfInitialBudget,
                                type: 'pdf',
                                initialBudgetTotal: content.initialBudgetTotal,
                                rawInitialBudgetTotal:
                                  content.rawInitialBudgetTotal,
                                negativeInitialBudgetCount:
                                  content.negativeInitialBudgetCount,
                                posteriorRequestedAmountTotal:
                                  content.posteriorRequestedAmountTotal,
                                rawPosteriorRequestedAmountTotal:
                                  content.rawPosteriorRequestedAmountTotal,
                                negativePosteriorRequestedAmountCount:
                                  content.negativePosteriorRequestedAmountCount,
                                isDebugMode,
                              },
                            ),
                          textStyle: MONEY_STYLE,
                        };
                      }
                      default: {
                        const value = content[key];
                        const moneysKeys: z.infer<typeof format1ColumnType>[] =
                          [
                            'initialBudgetTotal',
                            'posteriorRequestedAmountTotal',
                          ];
                        const isMoney = moneysKeys.includes(key);

                        // 比較用の生の値を取得
                        const getRawValue = (key: string) => {
                          switch (key) {
                            case 'initialBudgetTotal':
                              return content.rawInitialBudgetTotal;
                            case 'posteriorRequestedAmountTotal':
                              return content.rawPosteriorRequestedAmountTotal;
                            default:
                              return null;
                          }
                        };

                        return {
                          value: isMoney
                            ? typeof value !== 'number'
                              ? null
                              : formatAmountForPdf(Number(value), {
                                  isDebugMode,
                                  negativeCount:
                                    key === 'initialBudgetTotal'
                                      ? content.negativeInitialBudgetCount
                                      : key === 'posteriorRequestedAmountTotal'
                                        ? content.negativePosteriorRequestedAmountCount
                                        : 0,
                                  rawValue: getRawValue(key),
                                })
                            : value,
                          textStyle: isMoney ? MONEY_STYLE : undefined,
                        };
                      }
                    }
                  },
                ),
              }),
            ),
          ];
        },
      ),
    ];
  });
};

const getFormat1PdfFooter = ({
  summary,
}: {
  summary: z.infer<typeof ReportOrganizingTableFormat1Model>['summary'];
}): ReportTableProps['footer'] => {
  const summaries = [...summary.general, ...summary.special];
  const getSummaryRows = (type: SummaryTotalType) => {
    return summaries.map<TableBodyElement>((v, idx, arr) => {
      return {
        row: [
          ...(idx === 0
            ? [
                {
                  rowSpan: arr.length,
                  colSpan: 7,
                  value:
                    type === 'rsTotal'
                      ? t('review-sheet-total')
                      : type === 'skipRsTotal'
                        ? t('skip-administrative-reform-team-evaluation-total')
                        : t('total'),
                },
              ]
            : []),
          {
            value: format1ValueConverter.accountingType(
              v.accountingType,
              v.accountTitle,
            ),
          },
          {
            type: 'multiple',
            cell: {
              flexCells: [
                {
                  value: format1ValueConverter.summaryPriorFinalizedAmountTotal(
                    { values: v, fileType: 'pdf', totalType: type },
                  ),
                  textStyle: MONEY_STYLE,
                },
                {
                  value: format1ValueConverter.summaryPriorExecutionAmountTotal(
                    { values: v, fileType: 'pdf', totalType: type },
                  ),
                  textStyle: MONEY_STYLE,
                },
              ],
            },
          },
          {
            value: format1ValueConverter.summaryInitialBudgetTotal({
              values: v,
              fileType: 'pdf',
              totalType: type,
            }),
            textStyle: MONEY_STYLE,
          },
          {
            value: format1ValueConverter.summaryPosteriorRequestedAmountTotal({
              values: v,
              fileType: 'pdf',
              totalType: type,
            }),
            textStyle: MONEY_STYLE,
          },
          {
            value:
              format1ValueConverter.summaryDifferencePosteriorRequestedSelfInitialBudget(
                {
                  values: v,
                  fileType: 'pdf',
                  totalType: type,
                },
              ),
            textStyle: MONEY_STYLE,
          },
          { value: '' },
          { value: '' },
          {
            type: 'multiple',
            cell: {
              flexCells: [
                {
                  value:
                    type === 'rsTotal'
                      ? format1ValueConverter.summaryReflectedAmount({
                          values: v,
                          totalType: type,
                          fileType: 'pdf',
                        })
                      : '',
                  textStyle: MONEY_STYLE,
                },
                { value: '' },
              ],
            },
          },
          { value: '' },
          { value: '' },
          { value: '' },
        ],
      };
    });
  };

  return [
    ...getSummaryRows('rsTotal'),
    ...getSummaryRows('skipRsTotal'),
    ...getSummaryRows('total'),
  ];
};

export const getFormat1Pdf = ({
  year,
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  data: z.infer<typeof ReportOrganizingTableFormat1Model>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): {
  header: ReportTableProps['header'];
  body: ReportTableProps['data'];
  footer: ReportTableProps['footer'];
} => {
  return {
    header: getFormat1PdfHeader({ year }),
    body: getForma1PdfBody({
      policies: data.policies,
      isDebugMode,
      projectDetailUrlType,
    }),
    footer: getFormat1PdfFooter({ summary: data.summary }),
  };
};
